/**
 * Panneau d'inventaire: tout ce que le bonhomme transporte, groupe par
 * categorie, avec un bouton "Manger" sur ce qui se mange.
 */
import type { Inventaire, ItemDef, ItemId } from '../../shared/types/items.ts'
import { estComestible, objet } from '../../shared/data/objets.ts'
import { trier } from '../../shared/sim/inventaire.ts'
import { journal } from '../log/journal.ts'
import { Panneau, bouton, el, grille, section } from './commun.ts'

interface Groupe {
  categorie: ItemDef['categorie']
  titre: string
  icone: string
}

const GROUPES: readonly Groupe[] = [
  { categorie: 'matiere', titre: 'Matières', icone: '🪵' },
  { categorie: 'nourriture', titre: 'Nourriture', icone: '🍎' },
  { categorie: 'objet', titre: 'Objets', icone: '✨' },
  { categorie: 'outil', titre: 'Outils', icone: '🧰' },
]

export class PanneauInventaire extends Panneau {
  /** Le joueur veut manger cet objet. */
  onManger: ((id: ItemId) => void) | null = null

  private inv: Inventaire = {}
  private dansCuisine = false

  constructor(parent: HTMLElement) {
    super(parent, 'menu', 'Inventaire', '🎒', 'panneau-fond--inventaire')
  }

  ouvrir(inv: Inventaire, dansCuisine: boolean): void {
    this.inv = inv
    this.dansCuisine = dansCuisine
    this.construire()
    this.afficher(dansCuisine ? 'dans la cuisine' : 'dehors')
  }

  /** Reconstruit le contenu si le panneau est ouvert (apres avoir mange). */
  rafraichir(inv: Inventaire): void {
    this.inv = inv
    if (this.ouvert) this.construire()
  }

  private construire(): void {
    this.viderCorps()
    this.viderPied()

    const objets = trier(this.inv).filter((e) => e.n > 0)
    if (objets.length === 0) {
      this.corps.append(el('p', 'vide', 'Ton sac est vide. Va récolter du bois, des pierres et des fruits !'))
    }

    for (const g of GROUPES) {
      const dedans = objets.filter((e) => objet(e.id).categorie === g.categorie)
      if (dedans.length === 0) continue
      const s = section(g.titre, g.icone)
      const gr = grille('grille--objets')
      for (const { id, n } of dedans) gr.append(this.carte(id, n))
      s.append(gr)
      this.corps.append(s)
    }

    const total = objets.reduce((somme, e) => somme + e.n, 0)
    this.pied.append(el('span', 'pied__note', `${objets.length} sortes d’objets · ${total} au total`))
    if (this.dansCuisine) {
      this.pied.append(el('span', 'pied__astuce', '🍳 Tu es dans la cuisine: tu peux aussi préparer des plats.'))
    }
  }

  private carte(id: ItemId, n: number): HTMLElement {
    const def = objet(id)
    const carte = el('div', 'carte carte--objet')
    const haut = el('div', 'carte__haut')
    haut.append(el('span', 'carte__icone', def.icone))
    haut.append(el('span', 'carte__quantite', `× ${n}`))
    carte.append(haut)
    carte.append(el('span', 'carte__nom', def.nom))

    if (def.faim > 0 || def.energie > 0) {
      const effets = el('div', 'effets')
      if (def.faim > 0) effets.append(el('span', 'effet effet--faim', `+${def.faim} faim`))
      if (def.energie > 0) effets.append(el('span', 'effet effet--energie', `+${def.energie} énergie`))
      carte.append(effets)
    }

    // Tout ce qui est comestible se mange n'importe ou: le brut, les gateries
    // de la plage, et les plats cuisines qui sont deja prets.
    if (estComestible(id)) {
      carte.append(
        bouton('🍽️ Manger', () => {
          journal.input('menu', `manger ${id}`, { dansCuisine: this.dansCuisine, restant: n })
          this.onManger?.(id)
        }, 'bouton--vert carte__action'),
      )
    }
    return carte
  }

  override detruire(): void {
    this.onManger = null
    super.detruire()
  }
}
